import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  backgroundSelector,
  idSelector,
} from "../../../features/cartransferSlice";
import { backgroundCode } from "../../../api/parseDocsService";
import { Form, Input, Button, Row, Col, Alert } from "antd";
import { setEntity } from "../../../features/cartransferSlice";
import {
  parseFieldsToState,
  parseStateToFields,
} from "../../../utils/parseFormfields";
import UploadFile from "../../../utils/uploadFile";
import { toast } from "../../../utils/toasts";
import { handleError, getErrorMessage } from "../../../utils/errorHandler";
import LimitationsModal from "../components/LimitationsModal";
import {
  CarOutlined,
  FileSearchOutlined,
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

// ── Shared design tokens ─────────────────────────────────────────────
const ACCENT = "#0d9488";
const LIGHT  = "#f0fdfa";
const BORDER = "#99f6e4";

const PanelHeader = ({ icon, title, hasDoc, extra }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      background: LIGHT,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      borderLeft: `4px solid ${ACCENT}`,
      marginBottom: 0,
    }}
  >
    <span style={{ color: ACCENT, fontSize: 16 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 14, color: ACCENT, flex: 1 }}>
      {title}
    </span>
    {hasDoc && (
      <CheckCircleOutlined style={{ color: "#22c55e", fontSize: 15 }} />
    )}
    {extra}
  </div>
);

const FieldSection = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#b0b0b0",
      marginBottom: 10,
      marginTop: 4,
      paddingBottom: 6,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    {icon && <span style={{ fontSize: 11 }}>{icon}</span>}
    {label}
  </div>
);

const fi = { marginBottom: 10 };

const BackgroundStep = ({ label }) => {
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [techExpanded, setTechExpanded] = useState(false);

  const dispatch = useDispatch();
  const entity = "background";
  const background = useSelector(backgroundSelector);
  const cartransferId = useSelector(idSelector);
  const [limitationsModal, setLimitationsModal] = useState();
  const [limitations, setLimitations] = useState();

  useEffect(() => {
    if (background?.limitations && background.limitations.length > 0) {
      setLimitations(background.limitations);
      setLimitationsModal(true);
    }
  }, [background]);

  const hasTransferenciaEnTramite = background?.transferenciaEnTramite === true;
  const hasDoc = !!(background?.docUrl || background?.inscription);

  const handleFieldsChange = useCallback(
    (_, allFields) => {
      dispatch(setEntity({ entity, data: parseFieldsToState(allFields) }));
    },
    [dispatch]
  );

  const formFields = useMemo(() => parseStateToFields(background), [background]);

  const getFolio = useCallback(async () => {
    setLoading(true);
    setUploadError(null);
    try {
      const pdf = await backgroundCode(
        background.invoice,
        background.verification,
        cartransferId
      );
      dispatch(setEntity({ entity, data: pdf.data }));
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Error al obtener folio");
      handleError(error, toast, "Error al obtener folio", "getFolio");
      setUploadError(errorMessage);
    }
    setLoading(false);
  }, [background.invoice, background.verification, cartransferId, dispatch]);

  const handleUploadError = useCallback((error) => {
    setUploadError(error.message || "Error al subir el archivo");
  }, []);

  return (
    <div>
      <LimitationsModal
        open={limitationsModal}
        setVisible={setLimitationsModal}
        limitations={limitations}
      />

      {/* Panel header */}
      <PanelHeader
        icon={<CarOutlined />}
        title={label || "Cert. Antecedentes"}
        hasDoc={hasDoc}
      />

      {/* Form body */}
      <div
        style={{
          border: `1px solid ${ACCENT}30`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          padding: "16px 16px 10px",
          background: "#fff",
        }}
      >
        <Form
          name={entity}
          onFieldsChange={handleFieldsChange}
          fields={formFields}
          layout="vertical"
        >
          {/* ── SUBIR DOCUMENTO ───────────────────────────── */}
          <FieldSection icon={<FilePdfOutlined />} label="Subir documento" />
          <div
            style={{
              border: `1.5px dashed ${hasDoc ? "#22c55e" : "#d1d5db"}`,
              borderRadius: 8,
              padding: "10px 14px",
              background: hasDoc ? "#f0fdf4" : "#fafafa",
              marginBottom: 10,
              transition: "all 0.2s",
            }}
          >
            <Form.Item style={{ marginBottom: 0 }}>
              {useMemo(
                () => (
                  <UploadFile
                    type="application/pdf"
                    entity={entity}
                    action="/parse-docs/fetch-background"
                    label="Subir PDF de antecedentes"
                    cartransferId={cartransferId}
                    onError={handleUploadError}
                  />
                ),
                [cartransferId, handleUploadError]
              )}
            </Form.Item>
            {background?.docUrl && (
              <a
                href={background.docUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, color: ACCENT }}
              >
                Ver PDF subido
              </a>
            )}
          </div>

          {/* ── EXTRACCIÓN POR FOLIO ──────────────────────── */}
          <FieldSection icon={<FileSearchOutlined />} label="Extracción por folio" />
          <Row gutter={12}>
            <Col span={11}>
              <Form.Item name="invoice" label="Número de folio" style={fi}>
                <Input size="middle" placeholder="Ej: 1234567" />
              </Form.Item>
            </Col>
            <Col span={13}>
              <Form.Item name="verification" label="Código de verificación" style={fi}>
                <Input size="middle" placeholder="Código del documento" />
              </Form.Item>
            </Col>
          </Row>
          <Button
            type="primary"
            block
            onClick={getFolio}
            loading={loading}
            style={{
              background: ACCENT,
              borderColor: ACCENT,
              marginBottom: 10,
              height: 36,
            }}
          >
            Extraer desde Registro Civil
          </Button>

          {uploadError && (
            <Alert
              message="Error al procesar el PDF"
              description={uploadError}
              type="error"
              showIcon
              style={{ marginBottom: 10 }}
              closable
              onClose={() => setUploadError(null)}
            />
          )}
          {hasTransferenciaEnTramite && (
            <Alert
              message="Transferencia en trámite"
              description="El vehículo registra una solicitud de transferencia en trámite."
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 10 }}
              closable
            />
          )}

          {/* ── VEHÍCULO ──────────────────────────────────── */}
          <FieldSection icon={<CarOutlined />} label="Datos del vehículo" />
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="inscription" label="Patente" style={fi}>
                <Input size="middle" placeholder="AB-1234" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="type" label="Tipo de vehículo" style={fi}>
                <Input size="middle" placeholder="Automóvil, Camioneta…" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="gas" label="Combustible" style={fi}>
                <Input size="middle" placeholder="Gasolina…" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── PROPIETARIO ───────────────────────────────── */}
          <FieldSection icon={<UserOutlined />} label="Propietario registral" />
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="name" label="Nombre" style={fi}>
                <Input size="middle" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="rut" label="RUT" style={fi}>
                <Input size="middle" placeholder="12.345.678-9" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── DATOS TÉCNICOS (colapsable) ────────────────── */}
          <div
            onClick={() => setTechExpanded((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              padding: "7px 10px",
              background: techExpanded ? "#f0fdfa" : "#fafafa",
              border: `1px solid ${techExpanded ? BORDER : "#e8e8e8"}`,
              marginBottom: techExpanded ? 0 : 4,
              userSelect: "none",
              borderRadius: techExpanded ? "6px 6px 0 0" : 6,
            }}
          >
            <ToolOutlined style={{ color: techExpanded ? ACCENT : "#999", fontSize: 12 }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: techExpanded ? ACCENT : "#999" }}>
              Datos técnicos
            </span>
            <span style={{ marginLeft: "auto", color: "#aaa", fontSize: 10 }}>
              {techExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </span>
          </div>

          {techExpanded && (
            <div
              style={{
                border: `1px solid ${BORDER}`,
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                padding: "12px 12px 4px",
                background: "#fafffe",
                marginBottom: 4,
              }}
            >
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item name="year" label="Año" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item name="make" label="Marca" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item name="model" label="Modelo" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="motor" label="N° motor" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="chasis" label="N° chasis" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="serial" label="N° serie" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="vin" label="VIN" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="color" label="Color" style={fi}>
                    <Input size="middle" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export default BackgroundStep;
